import { store } from "redux/store";
import {
  updateCheckinSyncStatus,
  setCheckedIn,
  setCheckInLocationId,
  setPunchId,
  setBreakId,
  setIsOnBreak,
  enqueueCheckinSync,
} from "redux/actions";
import { getCheckinSyncQueue } from "redux/selectors";
import { CALL_API } from "common/API";
import {
  PUNCH_ACTIONS,
  STATUS_CODE,
  CHECKIN_SYNC_STATUS,
  CHECKIN_SYNC_CONFIG,
} from "common/Constants";

const activeTimers = new Map();

const genId = () =>
  `sync_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const backoffDelay = (retryCount) =>
  CHECKIN_SYNC_CONFIG.BASE_DELAY_MS *
  Math.pow(CHECKIN_SYNC_CONFIG.BACKOFF_FACTOR, retryCount);

// Best-effort connectivity check. Not 100% reliable (a browser can report
// "online" while genuinely unable to reach the internet), but it reliably
// catches the common offline-mode test case (WiFi off / airplane mode) and
// stops us wasting a real network attempt every poll while we already know
// there's no connection.
const isKnownOffline = () =>
  typeof navigator !== "undefined" &&
  "onLine" in navigator &&
  navigator.onLine === false;

// CALL_API catches axios errors internally and resolves instead of
// rejecting, and the raw axios call has no timeout configured — so a
// connection that dies mid-request (rather than failing cleanly) can hang
// this promise forever. Race it against a local timeout so a bad request
// always resolves one way or another within REQUEST_TIMEOUT_MS.
const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("SYNC_TIMEOUT")), ms)
    ),
  ]);

export const queueCheckIn = ({
  action,
  clientId,
  payload,
  locationName,
  locationId,
}) => {
  const state = store.getState();
  const queue = getCheckinSyncQueue(state);
  const today = new Date().toISOString().slice(0, 10);

  // If the same action (e.g. another PUNCH_IN) is tapped again the same
  // day while an earlier attempt for it hasn't synced yet, reuse that
  // entry's id instead of stacking a new one. CHECKIN_SYNC_ENQUEUE already
  // replaces-by-id, so this collapses repeated taps into "only the latest
  // attempt gets sent" rather than queuing duplicates that could each hit
  // the server separately.
  const existing = queue.find(
    (item) =>
      item.action === action &&
      item.createdAt?.slice(0, 10) === today &&
      item.status !== CHECKIN_SYNC_STATUS.SYNCED
  );

  if (existing) {
    clearTimeout(activeTimers.get(existing.id));
    activeTimers.delete(existing.id);
  }

  const entry = {
    id: existing ? existing.id : genId(),
    action,
    clientId,
    payload,
    locationName,
    // The location dropdown's own value (payload.loginlocationid is parseInt'd
    // for the API, which the Select can't match back to an option).
    locationId,
    status: CHECKIN_SYNC_STATUS.PENDING,
    retryCount: 0,
    // Match the time the server will record for this punch.
    createdAt: payload?.clientpunchtime || new Date().toISOString(),
    lastError: null,
    nextRetryAt: null,
    syncingSince: null,
    terminal: false,
  };
  store.dispatch(enqueueCheckinSync(entry));
  processEntry(entry.id);
  return entry;
};

const setStatus = (id, changes) => {
  store.dispatch(updateCheckinSyncStatus(id, changes));
};

const applySuccessSideEffects = (entry, response) => {
  const { code, punchActionInsertedId = 0, breakActionInsertedId = 0 } =
    response || {};
  if (code === STATUS_CODE.PUNCH_IN_SUCCESSFUL) {
    store.dispatch(setCheckedIn(true));
    store.dispatch(
      setCheckInLocationId(
        entry.locationId ?? String(entry.payload.loginlocationid)
      )
    );
    store.dispatch(setPunchId(punchActionInsertedId));
  } else if (code === STATUS_CODE.PUNCH_OUT_SUCCESSFUL) {
    store.dispatch(setCheckedIn(false));
    store.dispatch(setCheckInLocationId(""));
    store.dispatch(setPunchId(""));
  } else if (code === STATUS_CODE.BREAK_STARTED) {
    store.dispatch(setIsOnBreak(true));
    store.dispatch(setBreakId(breakActionInsertedId));
  } else if (code === STATUS_CODE.BREAK_ENDED) {
    store.dispatch(setIsOnBreak(false));
    store.dispatch(setBreakId(""));
  }
};

export const processEntry = async (id) => {
  const entry = getCheckinSyncQueue(store.getState()).find(
    (item) => item.id === id
  );
  if (!entry) return;
  if (
    entry.status !== CHECKIN_SYNC_STATUS.PENDING &&
    entry.status !== CHECKIN_SYNC_STATUS.FAILED
  ) {
    return;
  }

  // Don't burn a network attempt we already know will fail. Leave the
  // entry exactly as PENDING/FAILED — no timer, no request — and let the
  // "online" event / visibilitychange / next poll pick it up once we're
  // actually connected again. This is what stops the repeated calls while
  // offline.
  if (isKnownOffline()) {
    return;
  }

  clearTimeout(activeTimers.get(id));
  activeTimers.delete(id);
  setStatus(id, {
    status: CHECKIN_SYNC_STATUS.SYNCING,
    syncingSince: new Date().toISOString(),
  });

  let response;
  try {
    response = await withTimeout(
      CALL_API(`punch-in-out/${entry.clientId}`, "post", entry.payload),
      CHECKIN_SYNC_CONFIG.REQUEST_TIMEOUT_MS
    );
  } catch (err) {
    response = null;
  }

  // Guard against a stale/timed-out response landing after this entry was
  // already superseded (queueCheckIn reused the id) or removed.
  const stillCurrent = getCheckinSyncQueue(store.getState()).find(
    (item) => item.id === id
  );
  if (!stillCurrent || stillCurrent.status !== CHECKIN_SYNC_STATUS.SYNCING) {
    return;
  }

  const code = response?.code;
  const success = [
    STATUS_CODE.PUNCH_IN_SUCCESSFUL,
    STATUS_CODE.PUNCH_OUT_SUCCESSFUL,
    STATUS_CODE.BREAK_STARTED,
    STATUS_CODE.BREAK_ENDED,
  ].includes(code);

  if (success) {
    applySuccessSideEffects(entry, response);
    setStatus(id, {
      status: CHECKIN_SYNC_STATUS.SYNCED,
      lastError: null,
      nextRetryAt: null,
      syncingSince: null,
      terminal: false,
    });
    return;
  }

  const isTerminalRejection = [
    STATUS_CODE.OUTSIDE_ALLOWED_PROXIMITY,
    STATUS_CODE.RECORD_EXIST,
    STATUS_CODE.INVALID_PAYLOAD,
  ].includes(code);

  const nextRetryCount = entry.retryCount + 1;
  const errorMessage =
    response?.message ||
    (code
      ? `Server rejected sync (${code})`
      : "Network error — could not reach server");

  if (isTerminalRejection) {
    // A real, correctness-based rejection from the server — retrying won't
    // change the outcome, so this stops here permanently until the person
    // explicitly taps "Sync Now". It will NOT be picked up by the
    // automatic poll/online-event retry loop (see processQueue below).
    setStatus(id, {
      status: CHECKIN_SYNC_STATUS.FAILED,
      retryCount: nextRetryCount,
      lastError: errorMessage,
      nextRetryAt: null,
      syncingSince: null,
      terminal: true,
    });
    return;
  }

  if (nextRetryCount >= CHECKIN_SYNC_CONFIG.MAX_RETRIES) {
    // Exhausted retries, but this was a network-type failure (not a
    // rejection) — leave it eligible for automatic retry so it resumes
    // the moment connectivity is confirmed good again, instead of sitting
    // there requiring a manual tap.
    setStatus(id, {
      status: CHECKIN_SYNC_STATUS.FAILED,
      retryCount: nextRetryCount,
      lastError: errorMessage,
      nextRetryAt: null,
      syncingSince: null,
      terminal: false,
    });
    return;
  }

  const delay = backoffDelay(nextRetryCount - 1);
  setStatus(id, {
    status: CHECKIN_SYNC_STATUS.PENDING,
    retryCount: nextRetryCount,
    lastError: errorMessage,
    nextRetryAt: new Date(Date.now() + delay).toISOString(),
    syncingSince: null,
    terminal: false,
  });

  // Don't arm a local backoff timer while we already know we're offline —
  // that would just be another guaranteed-to-fail attempt on a fixed
  // schedule. The online event / poll will redrive it once we're back.
  if (!isKnownOffline()) {
    const timer = setTimeout(() => processEntry(id), delay);
    activeTimers.set(id, timer);
  }
};

// Any entry still marked SYNCING this long after it started is orphaned —
// either the request genuinely hung (belt-and-suspenders alongside the
// withTimeout above) or the tab was asleep/backgrounded mid-request. Kick
// it back to PENDING so the next processQueue() pass retries it instead of
// leaving the dashboard stuck on "Syncing..." indefinitely.
const reclaimStuckEntries = () => {
  const queue = getCheckinSyncQueue(store.getState());
  queue
    .filter(
      (item) =>
        item.status === CHECKIN_SYNC_STATUS.SYNCING &&
        (!item.syncingSince ||
          Date.now() - new Date(item.syncingSince).getTime() >
            CHECKIN_SYNC_CONFIG.STUCK_SYNCING_MS)
    )
    .forEach((item) => {
      setStatus(item.id, {
        status: CHECKIN_SYNC_STATUS.PENDING,
        syncingSince: null,
      });
    });
};

export const processQueue = () => {
  // Skip the whole cycle while definitely offline — nothing here can
  // succeed, so don't spend a request (or 12, per the Network tab) proving
  // that every 20 seconds.
  if (isKnownOffline()) return;

  reclaimStuckEntries();
  const queue = getCheckinSyncQueue(store.getState());
  queue
    .filter(
      (item) =>
        item.status === CHECKIN_SYNC_STATUS.PENDING ||
        // Only auto-retry FAILED entries that failed for network reasons.
        // Terminally-rejected entries (bad proximity, duplicate record,
        // invalid payload) stay FAILED until a manual "Sync Now" — endless
        // auto-retry can't fix a correctness problem, it just spams the
        // server.
        (item.status === CHECKIN_SYNC_STATUS.FAILED && !item.terminal)
    )
    .forEach((item) => processEntry(item.id));
};

export const syncNow = (id) => {
  const entry = getCheckinSyncQueue(store.getState()).find(
    (item) => item.id === id
  );
  if (!entry) return;
  clearTimeout(activeTimers.get(id));
  activeTimers.delete(id);
  // Manual tap always overrides the terminal flag — the person is
  // explicitly asking to try again (e.g. after moving into range, or after
  // a location fix on the backend).
  setStatus(id, {
    status: CHECKIN_SYNC_STATUS.PENDING,
    syncingSince: null,
    terminal: false,
  });
  processEntry(id);
};

let initialized = false;

export const initCheckInSyncManager = () => {
  if (initialized) return;
  initialized = true;

  // A fresh page load means any request that was previously "SYNCING" is
  // guaranteed dead — its JS execution context is gone (this covers the
  // "checked in, then closed the tab / logged out mid-sync" scenario:
  // whatever was in flight never got a chance to resolve, so on the next
  // load we always retry it rather than leaving it stuck forever).
  getCheckinSyncQueue(store.getState())
    .filter((item) => item.status === CHECKIN_SYNC_STATUS.SYNCING)
    .forEach((item) =>
      setStatus(item.id, {
        status: CHECKIN_SYNC_STATUS.PENDING,
        syncingSince: null,
      })
    );

  processQueue();

  // Best-effort: fires most of the time, cheap to keep.
  window.addEventListener("online", processQueue);

  // Safety net: `online` is not reliable in every environment (WebViews
  // in particular often never fire it), so also just check periodically
  // regardless of any event. isKnownOffline() inside processQueue makes
  // this a cheap no-op while genuinely offline, and it also doubles as the
  // stuck-SYNCING watchdog via reclaimStuckEntries() above.
  setInterval(processQueue, CHECKIN_SYNC_CONFIG.POLL_INTERVAL_MS);

  // Also retry the moment the app/tab comes back to the foreground —
  // covers "walked back into WiFi range while the app was backgrounded".
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") processQueue();
  });
};